import React, { useEffect, useState, useRef } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { Eye, Flame, Play, BarChart3, Instagram, Heart, CalendarDays, Clapperboard, Layers, ArrowRight, TrendingUp } from 'lucide-react';
import { GlassCard } from '../_shared/GlassCard';
import { Footer } from '../_shared/Footer';

/* ----------------------------------------------------------------------------
   DATA: Month 1 of the engagement, 31 Aug to 30 Sep 2026, @officialabhiifit.
   Instagram scraped live on 2026-10-08 with Apify (public play/like/comment counts,
   not private reach). Only videos and carousels produced by SocialSEO are counted,
   matched against the AbhiFit Media Tracker; Abhinav's own personal posts are excluded.
---------------------------------------------------------------------------- */

const M1 = { reels: 25, carousels: 15, views: 30165, median: 685, avg: 1160, likes: 815, comments: 101, octMedian: 1520 };

const fmtK = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);

const START = new Date(Date.UTC(2026, 7, 31));
const label = (i) => {
    const d = new Date(START.getTime() + i * 86400000);
    return `${d.getUTCDate()} ${d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })}`;
};

const DAILY_VIEWS = [690, 923, 1719, 596, 0, 881, 443, 1056, 560, 681, 533, 1975, 573, 0, 0, 0, 1582, 473, 381, 0, 489, 385, 0, 440, 7707, 1398, 2325, 509, 697, 733, 2416];
const DAILY_HOOK = [
    'Parents, falls and strength', 'I was the client nobody could fix', 'Your gut has more bacteria than cells', 'Ask this before you pay for any program', 'No reel',
    'Pre-workout nutrition, simply', 'The best diet is the one you keep', 'Pain does not always mean damage', 'Three strong sessions beat six', 'Whey vs real food',
    'Weekend lie-ins do not clear sleep debt', 'Sweeteners and insulin', 'Keep the muscle on GLP-1s', 'No reel', 'No reel',
    'No reel', 'Recovery tools, graded', 'Fat-loss tactics, graded', 'Four quadrants, four outcomes', 'No reel',
    'One hard set, twice a week', 'The comeback protocol', 'No reel', 'Your metabolism did not die at 40', 'The gym does not burn the most calories',
    'Nobody ruined coffee', 'It is not her metabolism', 'More gym days is not more results', 'Protein per meal', 'Resting less is not training harder',
    'Lost 12kg on junk food',
];
const DAILY = DAILY_VIEWS.map((v, i) => ({ i, day: label(i), views: v, hook: DAILY_HOOK[i] }));

const REEL_DAYS = new Set(DAILY_VIEWS.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0));
const CAROUSEL_DAYS = new Set([10, 11, 12, 13, 14, 15, 16, 17, 19, 20, 21, 22, 23, 24, 27]);

const TOP_REELS = [
    { hook: 'Stop thinking the gym burns the most calories', plays: '7,707', likes: '27', comments: '3', url: 'https://www.instagram.com/p/DdrDMbpDbXM/', gradient: 'from-indigo-600 via-violet-500 to-fuchsia-500', date: 'Sep 24' },
    { hook: 'He lost 12kg eating only junk food', plays: '2,416', likes: '22', comments: '1', url: 'https://www.instagram.com/p/Dd6WjMeDbh4/', gradient: 'from-violet-600 via-purple-500 to-pink-500', date: 'Sep 30' },
    { hook: "It's not her metabolism", plays: '2,325', likes: '23', comments: '3', url: 'https://www.instagram.com/p/DdwcNjSCDRi/', gradient: 'from-blue-600 via-indigo-500 to-violet-500', date: 'Sep 26' },
    { hook: 'Sweeteners behave like water on insulin', plays: '1,975', likes: '16', comments: '6', url: 'https://www.instagram.com/p/DdJF7ldjncq/', gradient: 'from-sky-600 via-blue-500 to-indigo-500', date: 'Sep 11' },
    { hook: 'Recovery tools, graded by evidence and cost', plays: '1,582', likes: '37', comments: '1', url: 'https://www.instagram.com/p/DdWzPdwDNGk/', gradient: 'from-purple-600 via-fuchsia-500 to-rose-500', date: 'Sep 16' },
];

const TOP_CAROUSELS = [
    { hook: 'Rajan, 42. Fit outside. A stranger in the bedroom.', likes: 133, comments: 23, url: 'https://www.instagram.com/p/DdHGnvsFNez/', date: 'Sep 10' },
    { hook: "Abs don't mean he knows what he's doing", likes: 43, comments: 4, url: 'https://www.instagram.com/p/Ddy_lj7E9xQ/', date: 'Sep 27' },
    { hook: "You're not tired because you need more caffeine", likes: 32, comments: 5, url: 'https://www.instagram.com/p/DdOmDmhFKxe/', date: 'Sep 13' },
];

/* ---------------------------------------------------------------- count-up hook */
const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);
    useEffect(() => {
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    if (reduced) { setCount(end); return; }
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const KPICard = ({ icon: Icon, iconColor, label, value, subtext }) => {
    const [animatedValue, ref] = useCounter(value);
    return (
        <GlassCard className="flex flex-col justify-between min-h-[170px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-4xl font-black text-white tracking-tight tabular-nums">
                    {animatedValue.toLocaleString()}
                </p>
                <p className="text-white/50 text-sm mt-3">{subtext}</p>
            </div>
        </GlassCard>
    );
};

/* ------------------------------------------------------------ reel/video tiles */
const ReelTile = ({ reel, rank, footerSub }) => (
    <a
        href={reel.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-[2rem] overflow-hidden transition-transform duration-300 hover:scale-[1.03] cursor-pointer"
        style={{ aspectRatio: '9/16' }}
    >
        <div className={`absolute inset-0 bg-gradient-to-br ${reel.gradient}`} />
        <span className="absolute -bottom-6 -right-2 text-[9rem] font-black text-white/10 leading-none select-none">{rank}</span>
        <div className="absolute top-3 left-3">
            <span className="bg-black/40 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-full border border-white/10">#{rank}</span>
        </div>
        <div className="absolute top-3 right-3">
            <span className="bg-black/40 backdrop-blur-md text-white/70 text-[10px] font-semibold px-2 py-1 rounded-full border border-white/10">{reel.date}</span>
        </div>
        <div className="absolute top-1/3 left-3 right-3">
            <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
                <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{reel.hook}</p>
            </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                    <Play size={24} className="text-white ml-1" fill="white" />
                </div>
            </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-sm font-bold tabular-nums">{reel.plays || reel.views}</p>
            {footerSub && <p className="text-white/50 text-[11px] font-mono">{footerSub(reel)}</p>}
        </div>
    </a>
);


const Hero = () => (
    <section className="min-h-[80vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-12 relative z-10">
        <div className="space-y-6 max-w-5xl mx-auto w-full">
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-violet-400/30 bg-violet-500/10 backdrop-blur-md mb-2">
                <span className="text-sm font-medium text-violet-200 tracking-wide uppercase">Month 1 · 31 Aug to 30 Sep 2026 · @officialabhiifit</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-violet-100 to-white drop-shadow-lg leading-tight">
                Month One.<br />The Engine Is Running.
            </h1>
            <p className="text-lg md:text-xl text-violet-100/70 max-w-2xl mx-auto leading-relaxed">
                <span className="text-white font-semibold">{M1.reels} reels and {M1.carousels} carousels</span> went live in the first month. The myth-busting reels found their audience in the last week, and October has opened at <span className="text-white font-semibold">double September's median views</span>.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12 w-full">
                <KPICard icon={Clapperboard} iconColor="bg-violet-500/20 text-violet-200" label="Reels Published" value={M1.reels} subtext="One script, one video" />
                <KPICard icon={Layers} iconColor="bg-fuchsia-500/20 text-fuchsia-200" label="Carousels Published" value={M1.carousels} subtext="From 10 Sep onward" />
                <KPICard icon={Eye} iconColor="bg-indigo-500/20 text-indigo-200" label="Reel Views" value={M1.views} subtext={`${M1.avg.toLocaleString()} average per reel`} />
                <KPICard icon={Heart} iconColor="bg-pink-500/20 text-pink-200" label="Likes" value={M1.likes} subtext={`+ ${M1.comments} comments`} />
            </div>
            <p className="text-white/30 text-xs max-w-2xl mx-auto pt-6">
                Scraped live on 8 Oct 2026. Counts only the content we produced, matched against your Media Tracker, so your own personal posts are left out. Figures are public play, like and comment counts, not private reach.
            </p>
        </div>
    </section>
);

const CadenceCell = ({ posted, tone }) => (
    <div className={`h-5 w-full rounded-[4px] ${posted ? tone : 'bg-white/[0.06] border border-white/10'}`} />
);

const Cadence = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
                    <CalendarDays size={14} className="text-violet-300" />
                    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">Publishing Cadence · 31 Days</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">What Went Live, Day by Day</h2>
                <p className="text-white/50 max-w-2xl mx-auto">Every filled block is a post. Carousels started on 10 Sep once the first topics were in.</p>
            </div>
            <GlassCard>
                <div className="overflow-x-auto">
                    <div className="min-w-[640px]">
                        <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: '90px repeat(31, 1fr)' }}>
                            <div />
                            {DAILY.map((d) => <div key={d.i} className="text-center text-[9px] text-white/40 font-mono">{d.day.split(' ')[0]}</div>)}
                        </div>
                        <div className="grid gap-1 mb-2 items-center" style={{ gridTemplateColumns: '90px repeat(31, 1fr)' }}>
                            <div className="flex items-center gap-1.5 text-xs text-violet-200 font-semibold pr-2"><Clapperboard size={12} /> Reels</div>
                            {DAILY.map((d) => <CadenceCell key={d.i} posted={REEL_DAYS.has(d.i)} tone="bg-violet-400/80" />)}
                        </div>
                        <div className="grid gap-1 items-center" style={{ gridTemplateColumns: '90px repeat(31, 1fr)' }}>
                            <div className="flex items-center gap-1.5 text-xs text-fuchsia-200 font-semibold pr-2"><Layers size={12} /> Carousels</div>
                            {DAILY.map((d) => <CadenceCell key={d.i} posted={CAROUSEL_DAYS.has(d.i)} tone="bg-fuchsia-400/70" />)}
                        </div>
                    </div>
                </div>
                <p className="text-white/60 text-sm font-semibold tabular-nums text-right mt-5 pt-4 border-t border-white/5">{M1.reels} reels · {M1.carousels} carousels</p>
            </GlassCard>
        </div>
    </section>
);

const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-black/85 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3 text-left max-w-[240px]">
            <p className="text-white font-bold text-sm mb-1">{d.day}</p>
            <p className="text-violet-300 text-sm tabular-nums">{d.views.toLocaleString()} views</p>
            <p className="text-white/50 text-xs mt-1 leading-snug">{d.hook}</p>
        </div>
    );
};

const spikeLabel = (props) => {
    const { x, y, width, value } = props;
    if (value < 2000) return null;
    return <text x={x + width / 2} y={y - 8} fill="#ddd6fe" fontSize={11} fontWeight={700} textAnchor="middle">{fmtK(value)}</text>;
};

const DailyChart = () => (
    <section className="py-4 px-4">
        <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
                    <BarChart3 size={14} className="text-violet-300" />
                    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">Reel Views · Day by Day</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">Where the Month Turned</h2>
                <p className="text-white/50 max-w-2xl mx-auto">The first three weeks found the baseline. In the last week, the myth-busting reels started breaking out. Hover any bar for the reel behind it.</p>
            </div>
            <GlassCard>
                <div className="h-[340px] w-full min-h-[340px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={DAILY} margin={{ top: 28, right: 10, left: -6, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={24} />
                            <YAxis tickFormatter={fmtK} tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} axisLine={false} tickLine={false} width={46} />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                            <Bar dataKey="views" fill="#a78bfa" radius={[4, 4, 0, 0]} minPointSize={2}>
                                <LabelList dataKey="views" content={spikeLabel} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </GlassCard>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-violet-300 mb-2 tabular-nums">7,707</p>
                    <p className="text-white/70 text-sm leading-relaxed">Views on the best reel, eleven times the typical reel. It overturned one belief people hold about the gym.</p>
                </GlassCard>
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-violet-300 mb-2 tabular-nums">{M1.median}</p>
                    <p className="text-white/70 text-sm leading-relaxed">Median views per reel in September. This is the baseline every new format is measured against.</p>
                </GlassCard>
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-violet-300 mb-2 tabular-nums">{M1.octMedian.toLocaleString()}</p>
                    <p className="text-white/70 text-sm leading-relaxed">Median views per reel in the first week of October. More than double September.</p>
                </GlassCard>
            </div>
        </div>
    </section>
);

const TopReels = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-400/30 bg-violet-500/10 backdrop-blur-md mb-4">
                    <Instagram size={15} className="text-violet-200" />
                    <span className="text-xs uppercase tracking-widest font-semibold text-violet-200">Top 5 Reels</span>
                </div>
                <p className="text-white/50 max-w-2xl mx-auto">Tap any card to watch. Views above, likes and comments below.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {TOP_REELS.map((reel, i) => <ReelTile key={i} reel={reel} rank={i + 1} footerSub={(r) => `${r.likes} likes · ${r.comments} comment${r.comments === '1' ? '' : 's'}`} />)}
            </div>
        </div>
    </section>
);

const TopCarousels = () => (
    <section className="py-8 px-4">
        <div className="container mx-auto max-w-5xl">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 text-center">Top Carousels</h3>
            <p className="text-white/50 text-center mb-10 max-w-xl mx-auto">The client story did three times the next best carousel.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {TOP_CAROUSELS.map((c, i) => (
                    <a key={i} href={c.url} target="_blank" rel="noopener noreferrer" className="block">
                        <GlassCard className="h-full hover:border-violet-400/40 transition-colors">
                            <p className="text-xs text-white/40 mb-2">#{i + 1} · {c.date}</p>
                            <p className="text-white font-bold leading-snug mb-4">{c.hook}</p>
                            <p className="text-violet-300 text-sm font-semibold tabular-nums">{c.likes} likes · {c.comments} comment{c.comments === 1 ? '' : 's'}</p>
                        </GlassCard>
                    </a>
                ))}
            </div>
        </div>
    </section>
);

const Learnings = () => {
    const items = [
        { title: 'One belief, overturned', body: 'All three top reels take one thing people are sure of and prove it wrong with a single fact: the gym is not where you burn the most, a man lost 12kg on junk food, her metabolism is not the problem. These sit at 3 to 11 times the typical reel. This is the lead format for month two.' },
        { title: 'Client stories win the carousels', body: 'The Rajan story pulled 133 likes and 23 comments, three times any other carousel. Real clients, with consent, are the strongest material on your page, the same pattern your own best posts showed before we started.' },
        { title: 'Momentum is real', body: 'September closed stronger than it opened, and October is running at more than double September\'s median. The format shift in the last week of September is the reason.' },
    ];
    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-5xl">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">What Month One Taught Us</h2>
                <p className="text-white/50 text-center mb-10 max-w-2xl mx-auto">Every post is scored after it goes live, and the lessons go straight into the next batch.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {items.map((it, i) => (
                        <GlassCard key={i}>
                            <div className="flex items-center gap-2 mb-3"><Flame size={16} className="text-violet-300" /><p className="text-lg font-bold text-white">{it.title}</p></div>
                            <p className="text-white/70 text-sm leading-relaxed">{it.body}</p>
                        </GlassCard>
                    ))}
                </div>
            </div>
        </section>
    );
};

const NextMonth = () => {
    const plan = [
        'Thirty reels, led by the one-belief-overturned format.',
        'Thirty carousels, with client stories as the anchor.',
        'The chaptered AbhiiFit playbook as the one resource every post points to.',
    ];
    const asks = [
        'One recording slot a week, so the pipeline never runs dry.',
        'Carousel topics and client stories you are happy to share, with consent.',
        'A 30-day Instagram Insights screenshot, so we report saves, shares, watch time and enquiries, the numbers you told us matter most.',
    ];
    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-6">
                <GlassCard>
                    <div className="flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-violet-300" /><p className="text-lg font-bold text-white">Month Two Plan</p></div>
                    <ul className="space-y-3">{plan.map((p, i) => <li key={i} className="flex gap-2 text-white/75 text-sm"><ArrowRight size={14} className="text-violet-300 shrink-0 mt-1" /><span>{p}</span></li>)}</ul>
                </GlassCard>
                <GlassCard>
                    <div className="flex items-center gap-2 mb-4"><Play size={16} className="text-fuchsia-300" /><p className="text-lg font-bold text-white">What We Need From You</p></div>
                    <ul className="space-y-3">{asks.map((p, i) => <li key={i} className="flex gap-2 text-white/75 text-sm"><ArrowRight size={14} className="text-fuchsia-300 shrink-0 mt-1" /><span>{p}</span></li>)}</ul>
                </GlassCard>
            </div>
        </section>
    );
};

export default function AbhinavReport1() {
    useEffect(() => { document.title = 'AbhiiFit Month 1 Report | SocialSEO'; }, []);
    return (
        <div className="font-sans leading-relaxed text-white relative" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');`}</style>
            <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="font-bold tracking-tight text-violet-300"><span className="text-white">Social</span>SEO</span>
                </div>
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="text-sm font-medium text-white/80">AbhiiFit · Month 1</span>
                </div>
            </header>
            <div className="relative z-10">
                <main>
                    <Hero />
                    <Cadence />
                    <DailyChart />
                    <TopReels />
                    <TopCarousels />
                    <Learnings />
                    <NextMonth />
                </main>
                <Footer />
            </div>
        </div>
    );
}
